/**
 * Memory Panel
 *
 * Displays the AI therapist's memory for a conversation partner.
 * Thin wrapper around the shared MemoryCard component.
 * Used on the Partner Detail page (/partners/:id).
 */

import { useState } from "react";
import { MemoryCard } from "../memory/MemoryCard";
import { EditMemoryModal } from "./EditMemoryModal";
import type { SpeakerMemory } from "../../types/speakerMemory";
import {
  useResetSpeakerMemory,
  useClearSpeakerMemory,
  useRegenerateSpeakerMemory,
} from "../../hooks/useSpeakerMemories";

interface MemoryPanelProps {
  memory: SpeakerMemory;
  onRefresh: () => void;
}

export function MemoryPanel({ memory, onRefresh }: MemoryPanelProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const reset = useResetSpeakerMemory();
  const clear = useClearSpeakerMemory();
  const regenerate = useRegenerateSpeakerMemory();

  return (
    <>
      <MemoryCard
        title="Therapist Memory"
        memory={memory}
        onEdit={() => setShowEditModal(true)}
        onReset={async () => {
          if (!memory.conversation_partner_id) return;
          await reset.mutateAsync(memory.conversation_partner_id);
          onRefresh();
        }}
        onRegenerate={async () => {
          await regenerate.mutateAsync(memory.conversation_partner_id!);
          onRefresh();
        }}
        onClear={async () => {
          if (!memory.conversation_partner_id) return;
          await clear.mutateAsync(memory.conversation_partner_id);
          onRefresh();
        }}
        resetPending={reset.isPending}
        regeneratePending={
          regenerate.isPending || memory.regeneration_in_progress
        }
        clearPending={clear.isPending}
      />
      <EditMemoryModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        partnerId={memory.conversation_partner_id!}
        currentContent={memory.content}
        onSuccess={onRefresh}
      />
    </>
  );
}
